import { Link } from "react-router-dom";
import {
  Radio, ScanLine, Activity, Check, ArrowRight,
  HeartPulse, Microscope, Building2, Palmtree, Ship, FerrisWheel,
  KeyRound, CreditCard, MapPin, ShieldCheck, IdCard,
} from "lucide-react";
import { Reveal } from "@/components/Section";

const RFID_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/24d4qgsr_RFID.jpg";

const REASONS = [
  { icon: IdCard, label: "Device identification & issuance" },
  { icon: Activity, label: "Refill intelligence & usage accountability" },
  { icon: ScanLine, label: "Study participation & check-ins" },
];

const ENVIRONMENTS = [
  { icon: HeartPulse, label: "Healthcare Facilities" },
  { icon: Microscope, label: "Laboratories" },
  { icon: Building2, label: "Corporate Campuses" },
  { icon: Palmtree, label: "Resorts" },
  { icon: Ship, label: "Cruise Ships" },
  { icon: FerrisWheel, label: "Theme Parks" },
];

const CAPABILITIES = [
  { icon: KeyRound, label: "Secure Entry & Room Access" },
  { icon: IdCard, label: "Employee Identification" },
  { icon: CreditCard, label: "Cashless Purchases" },
  { icon: ScanLine, label: "Attendance & Check-Ins" },
  { icon: MapPin, label: "Zone Location Awareness" },
  { icon: ShieldCheck, label: "Emergency Accountability" },
];

const DATA_POINTS = [
  "Device issuance & participant enrollment",
  "Refill dates, sites & formulation batches",
  "Approximate refill quantity or reservoir status",
  "Replacement, loss & device return events",
  "Scheduled visits & participation duration",
  "Movement through reader-equipped study zones",
];

function ImageTag({ label }) {
  return (
    <span className="absolute top-4 left-4 z-20 font-mono text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 bg-black/70 backdrop-blur border border-white/15 text-zinc-200">
      {label}
    </span>
  );
}

export default function RFIDSection() {
  return (
    <section className="border-t border-zinc-800 overflow-hidden" data-testid="rfid-integration">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          {/* IMAGE */}
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div
                className="absolute -inset-8 blur-3xl opacity-60 pointer-events-none"
                style={{ background: "radial-gradient(circle at 40% 40%, rgba(0,229,255,0.22), transparent 60%)" }}
              />
              <div className="relative border border-zinc-800 overflow-hidden bg-black shadow-2xl shadow-cyan-500/10">
                <ImageTag label="Proven WearSpray Mechanics" />
                <img loading="lazy"
                  src={RFID_IMG}
                  alt="An RFID chip over-molded within the WearSpray reservoir face"
                  className="w-full h-auto object-cover"
                  data-testid="rfid-image"
                />
                <div className="absolute inset-0 pointer-events-none border border-white/5" />
              </div>
              <div
                className="absolute -bottom-6 left-4 md:left-8 max-w-[220px] bg-[#101010]/90 backdrop-blur border p-4 z-20"
                style={{ borderColor: "rgba(0,229,255,0.25)" }}
              >
                <div className="flex items-center gap-2" style={{ color: "#00e5ff" }}>
                  <ScanLine size={15} strokeWidth={1.5} />
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase">Over-Mold Integration</span>
                </div>
                <p className="font-body text-xs text-zinc-400 leading-relaxed mt-2">
                  Chip encapsulated during molding, with no impact on size, look, or dispensing.
                </p>
              </div>
            </div>
          </Reveal>

          {/* TEXT */}
          <div className="lg:col-span-6">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 mb-5" style={{ color: "#00e5ff" }}>
                <Radio size={14} strokeWidth={1.5} /> RFID &amp; NFC · Available Today
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
                RFID, built into the reservoir.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="space-y-5 mt-8">
                <p className="font-body text-zinc-300 text-lg leading-relaxed">
                  This is not a future concept. An RFID or NFC chip can be
                  over-molded directly inside the WearSpray reservoir today, a
                  straightforward manufacturing step with no impact on the size,
                  appearance, or dispensing function of the device. Paired with
                  compatible readers and software, the wearable becomes a secure
                  digital credential as well as a refillable delivery platform.
                </p>
                <p className="font-body text-zinc-400 leading-relaxed">
                  Because the chip lives inside the reservoir, the same RFID
                  capability carries over to every WearSpray format, the Velcro
                  and silicone bands, smartwatch integrations, and partner
                  designed products alike. Its most immediate benefit is simple
                  identification: each wearable can be recognized, its refills
                  confirmed, and its issuance recorded, all without showing any
                  personal information on the device.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
                {REASONS.map((r) => {
                  const Icon = r.icon;
                  return (
                    <div key={r.label} className="flex items-start gap-3 border border-zinc-800 bg-[#111] px-4 py-4">
                      <Icon size={17} strokeWidth={1.5} style={{ color: "#00e5ff" }} className="mt-0.5 shrink-0" />
                      <span className="font-body text-xs text-zinc-300 leading-snug">{r.label}</span>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>

        {/* RESEARCH CASE */}
        <Reveal delay={0.1}>
          <div
            className="relative mt-20 md:mt-28 border overflow-hidden bg-[#0b0b0b]"
            style={{ borderColor: "rgba(0,229,255,0.2)" }}
            data-testid="rfid-case-snapshot"
          >
            <div
              className="absolute -top-24 -right-24 w-80 h-80 blur-3xl opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(0,229,255,0.18), transparent 65%)" }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 p-8 md:p-12">
              <div className="lg:col-span-7">
                <span className="font-mono text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 mb-5" style={{ color: "#00e5ff" }}>
                  <Activity size={14} strokeWidth={1.5} /> Application · Community Health Research
                </span>
                <h3 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-snug">
                  From identification to research intelligence.
                </h3>
                <p className="font-body text-zinc-300 leading-relaxed mt-6">
                  In a community health program, bands would be provided to
                  participants across a region, for example in malaria protection
                  and data research. An RFID or NFC enabled WearSpray could connect
                  each band to a secure research record: documenting when a device
                  was issued, where it was refilled, and when a participant
                  returned for follow up, all without displaying personal
                  information. Readers at clinics and refill stations could give
                  researchers a clearer view of retention, refill frequency, and
                  community level participation.
                </p>
                <Link
                  to="/global-health"
                  data-testid="rfid-globalhealth-link"
                  className="inline-flex items-center gap-2 font-body text-sm font-medium mt-7 group"
                  style={{ color: "#00e5ff" }}
                >
                  Explore our Global Health vision
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="lg:col-span-5 lg:border-l lg:border-zinc-800 lg:pl-14">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-6">
                  Potential Data Collected
                </p>
                <ul className="space-y-4">
                  {DATA_POINTS.map((d) => (
                    <li key={d} className="flex items-start gap-3">
                      <span className="mt-0.5 w-5 h-5 shrink-0 flex items-center justify-center border" style={{ borderColor: "rgba(0,229,255,0.4)", color: "#00e5ff" }}>
                        <Check size={12} strokeWidth={2} />
                      </span>
                      <span className="font-body text-sm text-zinc-300 leading-snug">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>

        {/* ADDITIONAL ENVIRONMENTS */}
        <Reveal delay={0.1}>
          <div className="mt-20 md:mt-28" data-testid="rfid-environments">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-8">
              Additional Environments · Natural Extensions
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-zinc-800 border border-zinc-800">
              {ENVIRONMENTS.map((e) => {
                const Icon = e.icon;
                return (
                  <div
                    key={e.label}
                    className="bg-[#111] px-4 py-8 flex flex-col items-center text-center gap-3 hover:bg-[#161616] transition-colors duration-500 group"
                  >
                    <Icon size={26} strokeWidth={1.25} style={{ color: "#e6c068" }} />
                    <span className="font-body text-xs text-zinc-400 leading-snug group-hover:text-white transition-colors duration-500">{e.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14" data-testid="rfid-capabilities">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-8">
              What It Could Enable
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CAPABILITIES.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.label}
                    className="flex items-center gap-3 border border-zinc-800 bg-[#111] px-5 py-4 hover:border-[rgba(0,229,255,0.35)] transition-colors duration-500"
                  >
                    <div className="w-9 h-9 shrink-0 border flex items-center justify-center" style={{ borderColor: "rgba(0,229,255,0.35)", color: "#00e5ff" }}>
                      <Icon size={16} strokeWidth={1.5} />
                    </div>
                    <span className="font-body text-sm text-zinc-300 leading-snug">{c.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="font-body text-xs text-zinc-500 leading-relaxed mt-16 max-w-3xl mx-auto text-center border-t border-zinc-800 pt-8" data-testid="rfid-accuracy-note">
            <span className="font-medium text-zinc-400">Important accuracy note:</span> RFID
            does not automatically provide continuous location tracking
            everywhere. Location awareness depends on the type of RFID technology
            used, the placement of compatible readers, the software platform, and
            the organization's privacy and consent policies.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
